// The incoming "Talk now" bar, drawn in the voice call pill's design so staff
// recognise it as the same kind of interruption they already answer for phone calls
// (brief: .superpowers/sdd/2026-10-30-instant-video-pill/brief.md). Presentational
// only: InstantVideoHost owns the queue, the countdown clock and the API calls.
//
// Every visual token below is copied from src/components/callPill/CallPill.jsx with
// the line it came from named. That file is never edited and never imported: the call
// pill is drawn by its own host and exports nothing a second bar could reuse. Only the
// shared modules beside it are imported -- callLayers (one stacking order for
// everything that floats during a call) and callPillPosition (the resting spot).
import PropTypes from 'prop-types';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Box, Button, CircularProgress, IconButton, Stack, Tooltip, Typography, useMediaQuery } from '@mui/material';
import { keyframes } from '@mui/material/styles';
import Iconify from '../Iconify';
import { DEFAULT_POSITION, PILL_MARGIN, pixelPosition } from '../callPill/callPillPosition';
import { callLayer } from '../callPill/callLayers';

// CallPill.jsx L15-20, copied unchanged: the dot's ring while a call rings.
const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.55); }
  70% { box-shadow: 0 0 0 10px rgba(34, 197, 94, 0); }
  100% { box-shadow: 0 0 0 0 rgba(34, 197, 94, 0); }
`;
// The whole bar flashes, which the phone pill does not do: a video call has to be
// answered inside 45 seconds or it is gone, so it asks for the room's attention.
// rgba(0,167,111,...) is the theme's success.main.
const flash = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(0, 167, 111, 0.55); }
  70% { box-shadow: 0 0 0 14px rgba(0, 167, 111, 0); }
  100% { box-shadow: 0 0 0 0 rgba(0, 167, 111, 0); }
`;

// CallPill.jsx L49-50.
const PILL_WIDTH = 'min(560px, calc(100vw - 24px))';
const PILL_HEIGHT = 64;
// CallPill.jsx L24-31, the RINGING row: the dark bar with the green dot.
const BAR_BG = 'grey.900';
const DOT_BG = 'success.main';
// Gap between stacked bars when more than one patient is waiting.
const STACK_GAP_PX = 8;

// The one stable hook CallPill renders on its bar (CallPill.jsx L364). Checking for it
// is how the video bar stays clear of a phone call that is already on screen: both
// rest at top centre, so without this they would sit on top of each other.
export const VOICE_PILL_SELECTOR = '[data-testid="call-pill"]';
// A pill is 64 px tall with 8 px of air under it.
export const VOICE_PILL_OFFSET = 72;
// How often to re-check for the voice pill (it appears without a resize or a scroll).
const RECHECK_MS = 1000;
const REDUCED_MOTION = '(prefers-reduced-motion: reduce)';

const voicePillOnScreen = () => {
    try { return Boolean(document.querySelector(VOICE_PILL_SELECTOR)); } catch { return false; }
};

// CallPill.jsx L43: the same viewport shape pixelPosition expects, with `top` as the
// highest the bar may sit. The extra offset is the only difference from the call pill.
const viewport = () => ({
    width: window.innerWidth,
    height: window.innerHeight,
    top: PILL_MARGIN + (voicePillOnScreen() ? VOICE_PILL_OFFSET : 0),
});
const sameViewport = (a, b) => a.width === b.width && a.height === b.height && a.top === b.top;

// Top-left pixel spot for the stack, from the call pill's own default position
// (top centre) and its own pixelPosition maths. The video bar is not draggable.
function useRestingSpot(size) {
    const [vp, setVp] = useState(viewport);
    useEffect(() => {
        let frame = 0;
        const update = () => {
            frame = 0;
            const next = viewport();
            setVp((prev) => (sameViewport(prev, next) ? prev : next));
        };
        const schedule = () => { if (!frame) frame = window.requestAnimationFrame(update); };
        window.addEventListener('resize', schedule);
        const timer = setInterval(schedule, RECHECK_MS);
        return () => {
            window.removeEventListener('resize', schedule);
            clearInterval(timer);
            if (frame) window.cancelAnimationFrame(frame);
        };
    }, []);
    return pixelPosition(DEFAULT_POSITION, vp, size);
}

// Read directly rather than through useMediaQuery so a test can set the answer.
function useReducedMotion() {
    const [reduced, setReduced] = useState(() => {
        try { return window.matchMedia(REDUCED_MOTION).matches === true; } catch { return false; }
    });
    useEffect(() => {
        let query;
        try { query = window.matchMedia(REDUCED_MOTION); } catch { return undefined; }
        setReduced(query.matches === true);
        const onChange = (event) => setReduced(event?.matches === true);
        query.addEventListener?.('change', onChange);
        return () => query.removeEventListener?.('change', onChange);
    }, []);
    return reduced;
}

// CallPill.jsx L487-499, the primary (`a.primary`) branch of its action buttons.
const primaryActionSx = {
    flexShrink: 0,
    borderRadius: 999,
    textTransform: 'none !important',
    color: 'grey.900',
    bgcolor: 'common.white',
    borderColor: 'rgba(255,255,255,0.4)',
    '&:hover': { bgcolor: 'rgba(255,255,255,0.85)', color: 'grey.900' },
    '&.Mui-disabled': { bgcolor: 'rgba(255,255,255,0.6)', color: 'grey.700' },
};
// CallPill.jsx L455-465, the "+N others" button: the same ghost fill on the dark bar.
const ghostActionSx = {
    flexShrink: 0,
    minWidth: 0,
    borderRadius: 999,
    textTransform: 'none !important',
    color: 'common.white',
    bgcolor: 'rgba(255,255,255,0.16)',
    '&:hover': { bgcolor: 'rgba(255,255,255,0.32)' },
    '&.Mui-disabled': { color: 'rgba(255,255,255,0.4)' },
};

export default function InstantVideoPill({ calls, now, muted, onAccept, onDismiss, onToggleMute }) {
    const ref = useRef(null);
    // CallPill.jsx L340-346: measure the real bar, start from its own fallback size.
    const [size, setSize] = useState({ width: 360, height: PILL_HEIGHT });
    useLayoutEffect(() => {
        const el = ref.current;
        if (!el) return;
        const { offsetWidth: width, offsetHeight: height } = el;
        if (width && height && (width !== size.width || height !== size.height)) setSize({ width, height });
    });
    const pos = useRestingSpot(size);
    const reducedMotion = useReducedMotion();
    // Narrow screens: the actions lose their words and keep their aria-labels.
    const compact = useMediaQuery('(max-width:600px)');

    if (!calls.length) return null;
    return (
        <Stack
            ref={ref}
            role="region"
            aria-label="Incoming video calls"
            spacing={`${STACK_GAP_PX}px`}
            sx={{
                position: 'fixed',
                left: pos.left,
                top: pos.top,
                zIndex: callLayer('PILL'),
                width: PILL_WIDTH,
                transition: 'left 180ms ease-out, top 180ms ease-out',
            }}
        >
            {calls.map((call) => {
                const secondsLeft = Math.max(0, Math.ceil((call.expiresAt - now) / 1000));
                const name = call.firstName || 'A patient';
                const settled = call.state === 'taken';
                const accepting = call.state === 'accepting';
                const flashing = call.state === 'ringing' && !reducedMotion;
                const acceptLabel = accepting ? 'Connecting…' : 'Accept';
                return (
                    <Box
                        key={call.id}
                        data-testid={`instant-video-pill-${call.id}`}
                        data-state={call.state}
                        data-flashing={flashing ? 'true' : 'false'}
                        role="alertdialog"
                        aria-label={`${name} wants to talk now`}
                        sx={{
                            // CallPill.jsx L370-381, minus the drag/fade it needs and this does not.
                            width: '100%',
                            height: PILL_HEIGHT,
                            boxSizing: 'border-box',
                            display: 'flex',
                            alignItems: 'center',
                            px: 1.5,
                            borderRadius: 4,
                            bgcolor: BAR_BG,
                            color: 'common.white',
                            boxShadow: 8,
                            // Reduced motion keeps the green ring, just not moving.
                            ...(flashing
                                ? { animation: `${flash} 1.4s infinite` }
                                : { outline: call.state === 'ringing' ? '3px solid rgba(0, 167, 111, 0.55)' : 'none' }),
                        }}
                    >
                        <Stack direction="row" alignItems="center" spacing={1.25} sx={{ width: '100%', minWidth: 0 }}>
                            {/* CallPill.jsx L384-397: the same 36 px dot, a camera instead of a handset. */}
                            <Box
                                sx={{
                                    width: 36,
                                    height: 36,
                                    flexShrink: 0,
                                    borderRadius: '50%',
                                    bgcolor: DOT_BG,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    ...(flashing ? { animation: `${pulse} 1.4s infinite` } : null),
                                }}
                            >
                                <Iconify icon="eva:video-fill" width={20} />
                            </Box>
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                                <Typography noWrap sx={{ fontSize: 14, fontWeight: 600, color: 'common.white' }}>
                                    {name} wants to talk now
                                </Typography>
                                {call.notice ? (
                                    <Typography noWrap role="status" aria-live="polite" sx={{ fontSize: 12, color: 'common.white', opacity: 0.72 }}>
                                        {call.notice}
                                    </Typography>
                                ) : (
                                    <Typography
                                        noWrap
                                        role="timer"
                                        aria-label={`${secondsLeft} seconds left`}
                                        sx={{ fontSize: 12, color: 'common.white', opacity: 0.72 }}
                                    >
                                        {`Instant video call · ${secondsLeft}s`}
                                    </Typography>
                                )}
                            </Box>
                            {!settled && (
                                <Stack direction="row" alignItems="center" spacing={1} sx={{ flexShrink: 0 }}>
                                    <Button
                                        size="small"
                                        variant="contained"
                                        color="inherit"
                                        data-testid={`instant-video-accept-${call.id}`}
                                        aria-label={accepting ? 'Connecting' : 'Accept'}
                                        disabled={accepting}
                                        onClick={() => onAccept(call)}
                                        startIcon={accepting
                                            ? <CircularProgress size={14} color="inherit" />
                                            : <Iconify icon="eva:video-fill" width={16} />}
                                        sx={primaryActionSx}
                                    >
                                        {compact ? null : acceptLabel}
                                    </Button>
                                    <Button
                                        size="small"
                                        color="inherit"
                                        data-testid={`instant-video-dismiss-${call.id}`}
                                        aria-label="Dismiss"
                                        disabled={accepting}
                                        onClick={() => onDismiss(call)}
                                        sx={ghostActionSx}
                                    >
                                        {compact ? <Iconify icon="eva:close-fill" width={16} /> : 'Dismiss'}
                                    </Button>
                                    <Tooltip title={muted ? 'Unmute call sound' : 'Mute call sound'}>
                                        <IconButton
                                            size="small"
                                            aria-label={muted ? 'Unmute call sound' : 'Mute call sound'}
                                            onClick={onToggleMute}
                                            sx={{ flexShrink: 0, color: 'common.white' }}
                                        >
                                            <Iconify icon={muted ? 'eva:volume-off-fill' : 'eva:volume-up-fill'} width={18} />
                                        </IconButton>
                                    </Tooltip>
                                </Stack>
                            )}
                        </Stack>
                    </Box>
                );
            })}
        </Stack>
    );
}

InstantVideoPill.propTypes = {
    calls: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string.isRequired,
        firstName: PropTypes.string,
        expiresAt: PropTypes.number.isRequired,
        state: PropTypes.oneOf(['ringing', 'accepting', 'taken']).isRequired,
        notice: PropTypes.string,
    })).isRequired,
    now: PropTypes.number.isRequired,
    muted: PropTypes.bool.isRequired,
    onAccept: PropTypes.func.isRequired,
    onDismiss: PropTypes.func.isRequired,
    onToggleMute: PropTypes.func.isRequired,
};
